import axios from 'axios';
import { isTokenExpired, clearSession, isTokenValid } from './jwtHelper';

// URL da API - usa a variavel de ambiente; fallback para producao (api-phada).
// Nao hardcodar IP de dev aqui: um build feito com o IP fixo quebra em producao.
// Para dev local, defina REACT_APP_API_URL no .env.development(.local).
const API_URL = process.env.REACT_APP_API_URL || 'https://api-phada.sederapp.com.br';

const api = axios.create({ baseURL: API_URL, timeout: 120000 });

// ==========================================
// FLAG PARA EVITAR LOGOUTS EM CASCATA
// ==========================================
// Quando multiplas requisicoes estao em andamento e uma falha de auth
// acontece, todas tentam fazer logout ao mesmo tempo. Essa flag
// garante que apenas o primeiro logout seja executado.
let isRedirecting = false;

function forceLogout(source, details = '') {
  if (isRedirecting) {
    console.warn(`[LOGOUT] Ignorando logout duplicado. Fonte: ${source}. ${details}`);
    return;
  }
  isRedirecting = true;
  console.error(`[LOGOUT] ====== DESLOGANDO ======`);
  console.error(`[LOGOUT] Fonte: ${source}`);
  if (details) console.error(`[LOGOUT] Detalhes: ${details}`);
  console.error(`[LOGOUT] Stack:`, new Error().stack);
  clearSession();

  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
}

// Interceptor para adicionar token JWT em todas as requisicoes
api.interceptors.request.use(
  (config) => {
    // URLs que nao precisam de autenticacao (login, registro, etc)
    const urlsPublicas = ['/login', '/register', '/recuperar-senha', '/resetar-senha'];
    const isUrlPublica = urlsPublicas.some(url => config.url.includes(url));

    // Se ja esta redirecionando E nao e URL publica, bloquear requisicao
    if (isRedirecting && !isUrlPublica) {
      return Promise.reject(new Error('Sessao encerrada, redirecionando...'));
    }

    const token = localStorage.getItem('sessionToken');

    if (token) {
      // Verificar se token esta expirado ANTES de enviar (exceto para URLs publicas)
      if (!isUrlPublica && isTokenExpired(token)) {
        forceLogout(
          'REQUEST_INTERCEPTOR',
          `Token expirado antes de enviar requisicao para: ${config.url}`
        );
        return Promise.reject(new Error('Token expirado'));
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ==========================================
// INTERCEPTOR DE AUDITORIA AUTOMATICA
// ==========================================
// URLs que NAO devem ser logadas automaticamente
const urlsIgnoradas = [
  '/api/auditoria/',
  '/login',
  '/permissoes',
  '/minhas-lojas',
  '/fatura/status',
  '/version',
  '/planos'
];

// Mapeamento de URLs para nomes de recursos amigaveis
const mapeamentoRecursos = {
  'produto': 'produtos',
  'pessoa': 'clientes',
  'servico': 'servicos',
  'pedidovenda': 'vendas',
  'ordemcompra': 'compras',
  'contaspagar': 'contas_pagar',
  'contasreceber': 'contas_receber',
  'usuario': 'usuarios',
  'categoria': 'categorias',
  'marca': 'marcas',
  'unidade': 'unidades',
  'embalagem': 'embalagens',
  'empresa': 'empresa',
  'caixa': 'caixa',
  'formapagamento': 'formas_pagamento',
  'perfil': 'perfis',
  'notafiscal': 'notas_fiscais'
};

// Funcao para extrair nome do recurso da URL
const extrairRecurso = (url) => {
  if (!url) return 'desconhecido';

  // Remover query params
  const urlSemParams = url.split('?')[0];

  // Extrair partes da URL
  const partes = urlSemParams.split('/').filter(p => p && p !== 'v1' && p !== 'api');

  if (partes.length === 0) return 'desconhecido';

  // Pegar primeira parte como recurso base
  const recursoBase = partes[0].toLowerCase();

  // Verificar mapeamento
  return mapeamentoRecursos[recursoBase] || recursoBase;
};

// Funcao para extrair ID do recurso da URL ou resposta
const extrairRecursoId = (url, responseData) => {
  // Tentar pegar do response primeiro
  if (responseData) {
    if (responseData.id) return String(responseData.id);
    if (responseData.idusuario) return String(responseData.idusuario);
    if (responseData.idproduto) return String(responseData.idproduto);
    if (responseData.idpessoa) return String(responseData.idpessoa);
  }

  // Tentar pegar da URL
  const partes = url.split('/').filter(p => p);
  const ultimaParte = partes[partes.length - 1];

  // Se a ultima parte for um numero, e o ID
  if (/^\d+$/.test(ultimaParte)) {
    return ultimaParte;
  }

  return '';
};

// Funcao para determinar acao baseada no metodo HTTP
const determinarAcao = (method) => {
  switch (method?.toUpperCase()) {
    case 'POST': return 'CREATE';
    case 'PUT': return 'UPDATE';
    case 'PATCH': return 'UPDATE';
    case 'DELETE': return 'DELETE';
    default: return 'UPDATE';
  }
};

// Funcao para gerar descricao automatica
const gerarDescricao = (acao, recurso, recursoId) => {
  const acaoTexto = {
    'CREATE': 'Cadastrou',
    'UPDATE': 'Alterou',
    'DELETE': 'Excluiu'
  };

  const texto = acaoTexto[acao] || 'Modificou';
  const idTexto = recursoId ? ` #${recursoId}` : '';

  return `${texto} ${recurso}${idTexto}`;
};

// Funcao para registrar log de auditoria (async, nao bloqueia)
const registrarLogAutomatico = async (config, response) => {
  try {
    const url = config.url || '';
    const method = config.method || '';

    // Verificar se deve ignorar esta URL
    const deveIgnorar = urlsIgnoradas.some(urlIgnorada => url.includes(urlIgnorada));
    if (deveIgnorar) return;

    // Apenas logar POST, PUT, PATCH, DELETE
    const metodosLogaveis = ['post', 'put', 'patch', 'delete'];
    if (!metodosLogaveis.includes(method.toLowerCase())) return;

    // Extrair informacoes
    const recurso = extrairRecurso(url);
    const acao = determinarAcao(method);
    const recursoId = extrairRecursoId(url, response?.data);
    const descricao = gerarDescricao(acao, recurso, recursoId);

    // Preparar dados para o log
    const payload = {
      acao,
      recurso,
      recursoId,
      descricao,
      dadosAnteriores: '',
      dadosNovos: config.data ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data)) : ''
    };

    // Enviar log de forma assincrona (fire and forget)
    api.post('/api/auditoria/registrar', payload).catch(() => {
      // Silenciar erros de auditoria
    });

  } catch (e) {
    // Silenciar erros do interceptor de auditoria
  }
};

// URLs que NAO devem causar logout quando retornam erro 401
// Apenas URLs de login (para nao redirecionar quando credenciais sao invalidas)
// e auditoria (para nao causar loop)
const isUrlExcludedFromLogout = (url) => {
  const excludedPatterns = [
    '/login',
    '/api/auditoria/',
  ];
  return excludedPatterns.some(pattern => url.includes(pattern));
};

// Interceptor para tratar respostas de erro (401/403/429)
api.interceptors.response.use(
  (response) => {
    // Registrar log de auditoria para operacoes bem-sucedidas
    registrarLogAutomatico(response.config, response);
    return response;
  },
  (error) => {
    const url = error.config?.url || '';
    const status = error.response?.status;

    // Tratar rate limiting (429 Too Many Requests)
    if (status === 429) {
      return Promise.reject(error);
    }

    // ==========================================
    // APENAS 401 = LOGOUT (token invalido/expirado)
    // 403 = sem permissao, NAO deslogar
    // ==========================================
    if (status === 401) {
      console.error(`[API] 401 Unauthorized - URL: ${url}`);
      console.error(`[API] Resposta do servidor:`, error.response?.data);

      if (!isUrlExcludedFromLogout(url)) {
        forceLogout(
          'RESPONSE_INTERCEPTOR_401',
          `Servidor retornou 401 para: ${url} | Resposta: ${JSON.stringify(error.response?.data)}`
        );
      } else {
        console.warn(`[API] 401 em URL excluida (${url}), nao deslogando`);
      }
    }

    if (status === 403) {
      console.warn(`[API] 403 Forbidden (sem permissao, NAO desloga) - URL: ${url}`);
      console.warn(`[API] Resposta:`, error.response?.data);
    }

    return Promise.reject(error);
  }
);

/**
 * Verifica se o usuario esta autenticado
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem('sessionToken');
  return token && isTokenValid(token);
};

/**
 * Faz logout do usuario
 */
export const logout = () => {
  forceLogout('LOGOUT_MANUAL', 'Chamada direta a funcao logout()');
};

/**
 * Reseta a flag de redirecionamento (deve ser chamada na página de login)
 */
export const resetRedirectFlag = () => {
  isRedirecting = false;
};

// ==========================================
// GRAVACAO REPETIDA ENQUANTO A PRIMEIRA ESTA EM ANDAMENTO
// ==========================================
// Com o backend levando alguns segundos para responder, clique duplo no Salvar,
// Ctrl+Enter repetido ou reabrir o menu da linha mandava o mesmo POST de novo e
// o registro era gravado duas vezes (Pessoas na Cooktime, 03/10/2026; varias
// telas sem trava no botao). Aqui um POST/PUT IDENTICO (mesma rota, mesmos
// dados, mesmos params) a outro ainda sem resposta nao vai ao servidor: recebe
// a resposta do primeiro. Depois que o primeiro responde, um novo envio passa
// normalmente. Para enviar de proposito em paralelo, passe { semDedupe: true }
// no config.
const gravacoesEmAndamento = new Map();

['post', 'put'].forEach((metodo) => {
  const original = api[metodo].bind(api);
  api[metodo] = (url, data, config) => {
    if (config?.semDedupe || (typeof FormData !== 'undefined' && data instanceof FormData)) {
      return original(url, data, config);
    }
    let chave;
    try {
      chave = `${metodo} ${url} ${JSON.stringify(data ?? null)} ${JSON.stringify(config?.params ?? null)}`;
    } catch (e) {
      return original(url, data, config);
    }
    const emAndamento = gravacoesEmAndamento.get(chave);
    if (emAndamento) {
      console.warn(`[API] ${metodo.toUpperCase()} ${url} repetido enquanto o primeiro esta em andamento: reaproveitando a resposta`);
      return emAndamento;
    }
    const promessa = original(url, data, config).finally(() => {
      gravacoesEmAndamento.delete(chave);
    });
    gravacoesEmAndamento.set(chave, promessa);
    return promessa;
  };
});

export default api;
